import QueryBuilder from ".";

type ColumnType = (whereQuery: WhereQueryBuilder) => void

export type SubQueryType = (subQuery: QueryBuilder) => void

class WhereQueryBuilder {

    protected _where: any[][] = [];
    protected _uniqueParameterID: string;
    protected _parameters: { [name: string]: any } = {};

    constructor() {

        this._uniqueParameterID = Array.from(new Array(10), (_) => String.fromCharCode(Math.round(Math.random() * 25) + 65)).join('')
    }

    protected whereFactory(column: ColumnType | string, operator: string | null, needle: SubQueryType | any, statement: 'AND' | 'OR'): this {
        if(typeof column === 'function') {

            const whereQuery = new WhereQueryBuilder()

            column(whereQuery)

            this._parameters = {
                ...this._parameters,
                ...whereQuery.getParameters()
            }

            this._where.push([whereQuery.getWhereQueries(), statement, "BRACKET"])

        } else if(typeof needle === 'function') {

            const subQuery = new QueryBuilder()

            needle(subQuery)

            const query = subQuery.querying('select')

            this._parameters = {
                ...this._parameters,
                ...query.getParameters()
            }

            this._where.push([`${column} ${operator} (${query.getQuery()})`.trim(), statement])

        } else {

            const id = `${this._uniqueParameterID}${Object.keys(this._parameters).length + 1}`

            this._where.push([`${column} ${operator} ${
                ['IN', 'NOT IN'].includes(operator!) ? (
                    `(:...${id})`
                ) : (
                    ['BETWEEN', 'NOT BETWEEN'].includes(operator!) ? (
                        `:${id}1 AND :${id}2`
                    ) : (
                        `:${id}`
                    )
                )}`
            , statement])
    
            if(['BETWEEN', 'NOT BETWEEN'].includes(operator!)) {
                this._parameters[`${id}1`] = needle[0]
                this._parameters[`${id}2`] = needle[1]
            } else {
                this._parameters[id] = needle
            }
        }

        
        return this
    }
  
    public where(column: ColumnType | string, operator: string | SubQueryType | any, needle?: SubQueryType | any): this {

        return needle ? (
            this.whereFactory(column, operator, needle, 'AND')
        ) : (
            this.whereFactory(column, '=', operator, 'AND')
        )
    }

    public orWhere(column: ColumnType | string, operator: string | SubQueryType | any, needle?: SubQueryType | any): this {

        return needle ? (
            this.whereFactory(column, operator, needle, 'OR')
        ) : (
            this.whereFactory(column, '=', operator, 'OR')
        )
    }

    public whereAny(column: string, operator: string | SubQueryType, needle?: SubQueryType): this {

        return needle ? (
            this.whereFactory(column, `${operator} ANY`, needle, 'AND')
        ) : (
            this.whereFactory(column, '= ANY', operator, 'AND')
        )
    }

    public orWhereAny(column: string, operator: string | SubQueryType, needle?: SubQueryType): this {

        return needle ? (
            this.whereFactory(column, `${operator} ANY`, needle, 'OR')
        ) : (
            this.whereFactory(column, '= ANY', operator, 'OR')
        )
    }

    public whereAll(column: string, operator: string | SubQueryType, needle?: SubQueryType): this {

        return needle ? (
            this.whereFactory(column, `${operator} ALL`, needle, 'AND')
        ) : (
            this.whereFactory(column, '= ALL', operator, 'AND')
        )
    }

    public orWhereAll(column: string, operator: string | SubQueryType, needle?: SubQueryType): this {

        return needle ? (
            this.whereFactory(column, `${operator} ALL`, needle, 'OR')
        ) : (
            this.whereFactory(column, '= ALL', operator, 'OR')
        )
    }

    public whereSome(column: string, operator: string | SubQueryType, needle?: SubQueryType): this {

        return needle ? (
            this.whereFactory(column, `${operator} SOME`, needle, 'AND')
        ) : (
            this.whereFactory(column, '= SOME', operator, 'AND')
        )
    }

    public orWhereSome(column: string, operator: string | SubQueryType, needle?: SubQueryType): this {

        return needle ? (
            this.whereFactory(column, `${operator} SOME`, needle, 'OR')
        ) : (
            this.whereFactory(column, '= SOME', operator, 'OR')
        )
    }

    public whereExist(needle: SubQueryType): this {

        return this.whereFactory('', `EXISTS`, needle, 'AND')
    }

    public orWhereExist(needle: SubQueryType): this {

        return this.whereFactory('', `EXISTS`, needle, 'OR')
    }

    public whereNotExist(needle: SubQueryType): this {

        return this.whereFactory('NOT', `EXISTS`, needle, 'AND')
    }

    public orWhereNotExist(needle: SubQueryType): this {

        return this.whereFactory('NOT', `EXISTS`, needle, 'OR')
    }

    public whereIn(column: string, needle: SubQueryType | any[]): this {
        return this.where(column, 'IN', needle)
    }

    public whereNotIn(column: string, needle: SubQueryType | any[]): this {
        return this.where(column, 'NOT IN', needle)
    }

    public orWhereIn(column: string, needle: SubQueryType | any[]): this {
        return this.orWhere(column, 'IN', needle)
    }

    public orWhereNotIn(column: string, needle: SubQueryType | any[]): this {
        return this.orWhere(column, 'NOT IN', needle)
    }

    public whereBetween(column: string, needle: SubQueryType | any[]): this {
        return this.where(column, 'BETWEEN', needle)
    }

    public whereNotBetween(column: string, needle: SubQueryType | any[]): this {
        return this.where(column, 'NOT BETWEEN', needle)
    }

    public orWhereBetween(column: string, needle: SubQueryType | any[]): this {
        return this.orWhere(column, 'BETWEEN', needle)
    }

    public orWhereNotBetween(column: string, needle: SubQueryType | any[]): this {
        return this.orWhere(column, 'NOT BETWEEN', needle)
    }

    public getParameters() {
        return this._parameters
    }

    protected getWhereQueries() {
        return this._where
    }

}

export default WhereQueryBuilder